import {useCallback, useEffect, useRef, useState} from "react";
import {deleteCoupon, listCoupons, saveCoupon, toggleCoupon} from "./coupon-api";

export default function useCouponManagement(language) {
    const [coupons, setCoupons] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [busy, setBusy] = useState(null);
    const scope = useRef(null);
    const options = useCallback(current => ({signal: current.controller.signal,
        headers: {"Language-Context": (language || "en").toUpperCase()}}), [language]);

    const reload = useCallback(async () => {
        const current = scope.current;
        if (!current?.active || current.pending) return;
        const read = ++current.read;
        setLoading(true);
        setLoadError(null);
        try {
            const data = await listCoupons(options(current));
            if (current.active && current.read === read) setCoupons(data);
        } catch (error) {
            if (current.active && current.read === read) setLoadError(error.response?.data?.message || "CouponsLoadFailed");
        } finally {
            if (current.active && current.read === read) setLoading(false);
        }
    }, [options]);

    useEffect(() => {
        const current = {active: true, pending: false, read: 0, controller: new AbortController()};
        scope.current = current;
        reload();
        return () => { current.active = false; current.controller.abort(); };
    }, [reload]);

    // Pessimistic writes keep rows unchanged until the server confirms success; one write owns the UI lock.
    const mutate = async (action, request, apply) => {
        const current = scope.current;
        if (!current?.active || current.pending) return false;
        current.pending = true;
        current.read++;
        setLoading(false);
        setBusy(action);
        setNotice(null);
        try {
            const result = await request(options(current));
            if (!current.active) return false;
            apply(result);
            setNotice({severity: "success", message: "CouponChangeSaved"});
            return true;
        } catch (error) {
            if (current.active) setNotice({severity: "error", message: error.response?.data?.message || "CouponChangeFailed"});
            return false;
        } finally {
            current.pending = false;
            if (current.active) setBusy(null);
        }
    };

    const save = (form, existing) => mutate("save", opts => saveCoupon(form, existing, opts), result =>
        setCoupons(previous => existing ? previous.map(coupon => coupon.couponId === existing.couponId ? result : coupon) : [result, ...(previous || [])]));
    const remove = coupon => mutate("delete", opts => deleteCoupon(coupon, opts), () =>
        setCoupons(previous => previous.filter(item => item.couponId !== coupon.couponId)));
    const toggle = coupon => mutate("status", opts => toggleCoupon(coupon, opts), result =>
        setCoupons(previous => previous.map(item => item.couponId === coupon.couponId ? result : item)));

    const clearNotice = useCallback(() => setNotice(null), []);
    return {coupons, loading, loadError, notice, busy, reload, save, remove, toggle, clearNotice};
}
